/**
 * Parlay Signature Utilities
 * Shared logic for detecting duplicate parlays (order-independent matching)
 */

import type { LeagueKey } from './league';
import { canonicalizeStatType } from './statType';
import { resolveTeam } from './teams';

/**
 * Leg data needed to create a signature
 */
/**
 * A matchup separator standing on its own ("vs", "vs.", "@", "at"). Without the spaces, "at"
 * inside "Golden State" and "Seattle" split the name itself.
 */
export const MATCHUP_SEPARATOR = /\s+(?:vs\.?|@|at)\s+/i;

export interface LegSignatureInput {
  player: string;
  statType: string;
  line: string | number | null | undefined;
  overUnder: string | null | undefined;
  gameDate: string | Date | null | undefined;
  // Runtime data can drop leagueKey on a leg; keep the type honest so callers
  // stay null-safe (findTeam degrades to null rather than throwing).
  leagueKey: LeagueKey | null | undefined;
}

// Game prop StatKeys whose subject is a team name, normalized to abbreviations
const GAME_PROP_TYPES = ['totals', 'spreads', 'h2h'];

/** Whether a stat type is a game line (total, spread, moneyline) rather than a player stat. */
export function isGamePropStatType(statType: string): boolean {
  return GAME_PROP_TYPES.includes(canonicalizeStatType(statType));
}

/**
 * Normalize team name(s) for game-level props
 * - Totals: "Spurs vs Rockets" -> "HOU:SAS" (sorted alphabetically)
 * - Spreads/Moneyline: "Lakers" -> "LAL"
 */
function normalizeGamePropIdentifier(
  player: string,
  statKey: string,
  leagueKey: LeagueKey | null | undefined,
): string {
  const playerTrimmed = player.trim();

  // For totals, extract both teams and sort alphabetically
  if (statKey === 'totals') {
    const vsMatch = playerTrimmed.match(new RegExp(`^(.+?)${MATCHUP_SEPARATOR.source}(.+)$`, 'i'));
    if (vsMatch) {
      const team1 = resolveTeam(vsMatch[1].trim(), leagueKey);
      const team2 = resolveTeam(vsMatch[2].trim(), leagueKey);

      if (team1 && team2) {
        // Sort abbreviations alphabetically for consistent matching
        const abbrs = [team1.abbr, team2.abbr].sort();
        return abbrs.join(':').toLowerCase();
      }
    }
    // Fallback: might be "Game" or single identifier
    return playerTrimmed.toLowerCase();
  }

  // For spreads and moneyline, normalize single team name
  if (statKey === 'spreads' || statKey === 'h2h') {
    const team = resolveTeam(playerTrimmed, leagueKey);
    if (team) {
      return team.abbr.toLowerCase();
    }
  }

  // Fallback for unrecognized patterns
  return playerTrimmed.toLowerCase();
}

/**
 * Create a normalized signature for a leg (order-independent matching)
 * Uses player + statType + line + direction + gameDate for uniqueness
 *
 * Including gameDate ensures that:
 * - Same player/prop on different days = different parlays
 * - Tracking LeBron pts Over 25.5 on Monday != Tuesday
 *
 * For game-level props (totals, spreads, moneyline):
 * - Team names are normalized to abbreviations for consistent matching
 * - "Spurs vs Rockets" and "San Antonio Spurs vs Houston Rockets" will match
 */
export function createLegSignature(leg: LegSignatureInput): string {
  // Any spelling of a stat is its StatKey (e.g., "PTS+REB+AST" → "pra")
  const statKey = canonicalizeStatType(leg.statType || '');

  // For game props, normalize team names to abbreviations
  const normalizedPlayer = GAME_PROP_TYPES.includes(statKey)
    ? normalizeGamePropIdentifier(leg.player || '', statKey, leg.leagueKey)
    : (leg.player || '').toLowerCase().trim();
  const normalizedLine =
    leg.line !== null && leg.line !== undefined ? parseFloat(String(leg.line)).toFixed(1) : 'null';
  const normalizedDirection = (leg.overUnder || 'null').toLowerCase().trim();

  // Normalize gameDate to YYYY-MM-DD format
  let normalizedDate = 'null';
  if (leg.gameDate) {
    const date = leg.gameDate instanceof Date ? leg.gameDate : new Date(leg.gameDate);
    if (!Number.isNaN(date.getTime())) {
      // allow-utc-date: legs store the game day at noon UTC, so its UTC day is the game day
      normalizedDate = date.toISOString().split('T')[0];
    }
  }

  return `${normalizedPlayer}:${statKey}:${normalizedLine}:${normalizedDirection}:${normalizedDate}`;
}

/**
 * Create a Set of signatures for all legs in a parlay
 * Order-independent: two parlays with same legs in different order will have same signature set
 */
export function createParlaySignatureSet(legs: LegSignatureInput[]): Set<string> {
  return new Set(legs.map(createLegSignature));
}

/**
 * Check if two parlays have the exact same legs (order-independent)
 * Returns true if all legs match exactly (same count, same signatures)
 */
export function parlaysHaveSameLegs(
  legs1: LegSignatureInput[],
  legs2: LegSignatureInput[],
): boolean {
  if (legs1.length !== legs2.length) return false;

  const set1 = createParlaySignatureSet(legs1);
  const set2 = createParlaySignatureSet(legs2);

  if (set1.size !== set2.size) return false;

  for (const sig of set1) {
    if (!set2.has(sig)) return false;
  }

  return true;
}

/**
 * Check if a parlay (represented by its legs) is already tracked.
 * Compares against a list of tracked parlays using order-independent signature matching.
 *
 * @param parlayLegs - Legs of the parlay to check (e.g., from a generated pick)
 * @param trackedParlayLegs - Array of tracked parlay leg arrays to compare against
 */
export function isParlayInTrackedList(
  parlayLegs: LegSignatureInput[],
  trackedParlayLegs: LegSignatureInput[][],
): boolean {
  if (parlayLegs.length === 0) return false;
  const parlaySignatures = createParlaySignatureSet(parlayLegs);

  return trackedParlayLegs.some((tpLegs) => {
    if (tpLegs.length !== parlayLegs.length) return false;
    const tpSignatures = createParlaySignatureSet(tpLegs);
    if (parlaySignatures.size !== tpSignatures.size) return false;
    for (const sig of parlaySignatures) {
      if (!tpSignatures.has(sig)) return false;
    }
    return true;
  });
}

/**
 * Create a stable key for a parlay based on its legs.
 * Useful for tracking in-progress operations per-parlay.
 */
export function createParlayKey(legs: LegSignatureInput[]): string {
  return [...createParlaySignatureSet(legs)].sort().join('|');
}
